import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle, Circle, Archive, Trash2 } from 'lucide-react';

interface StatusDropdownProps {
    currentStatus: string;
    onStatusChange: (status: any) => void;
    onDelete?: () => void;
}

const StatusDropdown: React.FC<StatusDropdownProps> = ({ currentStatus, onStatusChange, onDelete }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);

    const updateMenuPosition = () => {
        if (!buttonRef.current) return;
        const rect = buttonRef.current.getBoundingClientRect();
        setMenuPosition({
            top: rect.bottom + 4,
            left: rect.right - 128,
        });
    };

    // Close when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            const clickedTrigger = dropdownRef.current?.contains(target);
            const clickedMenu = menuRef.current?.contains(target);
            if (!clickedTrigger && !clickedMenu) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useLayoutEffect(() => {
        if (!isOpen) return;
        updateMenuPosition();

        const handleReposition = () => updateMenuPosition();
        window.addEventListener('resize', handleReposition);
        window.addEventListener('scroll', handleReposition, true);

        return () => {
            window.removeEventListener('resize', handleReposition);
            window.removeEventListener('scroll', handleReposition, true);
        };
    }, [isOpen]);

    const statusOptions = [
        { value: 'open', label: 'Open', icon: <Circle size={14} className="text-blue-500" /> },
        { value: 'closed', label: 'Closed', icon: <CheckCircle size={14} className="text-emerald-500" /> },
        { value: 'archived', label: 'Archived', icon: <Archive size={14} className="text-gray-400" /> },
    ];

    const currentOption = statusOptions.find(o => o.value === currentStatus) || statusOptions[0];

    return (
        <div className="relative inline-block text-left" ref={dropdownRef}>
            {/* The Trigger Button */}
            <button
                ref={buttonRef}
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    if (!isOpen) {
                        updateMenuPosition();
                    }
                    setIsOpen(!isOpen);
                }}
                className="inline-flex items-center gap-2 px-4 py-1.5 bg-white border border-gray-200 text-gray-700 rounded-md text-[12px] font-bold hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"
            >
                {currentOption.icon}
                <span className="tracking-tight">{currentOption.label}</span>
            </button>

            {/* The Actual Dropdown Menu */}
            {isOpen && menuPosition && createPortal(
                <div
                    ref={menuRef}
                    className="fixed w-32 bg-white border border-gray-200 rounded-lg shadow-xl z-[2500] py-1 overflow-hidden"
                    style={{ top: menuPosition.top, left: menuPosition.left }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {statusOptions.map((option) => (
                        <button
                            key={option.value}
                            onClick={(e) => {
                                e.stopPropagation();
                                onStatusChange(option.value);
                                setIsOpen(false);
                            }}
                            className={`w-full flex items-center gap-2 px-3 py-2 text-[12px] font-bold hover:bg-gray-50 transition-colors ${currentStatus === option.value ? 'text-indigo-600 bg-indigo-50/50' : 'text-gray-600'
                                }`}
                        >
                            {option.icon}
                            {option.label}
                        </button>
                    ))}
                    {onDelete && (
                        <>
                            <div className="my-1 border-t border-gray-100" />
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDelete();
                                    setIsOpen(false);
                                }}
                                className="w-full flex items-center gap-2 px-3 py-2 text-[12px] font-bold text-red-600 hover:bg-red-50 transition-colors"
                            >
                                <Trash2 size={14} className="text-red-500" />
                                Delete
                            </button>
                        </>
                    )}
                </div>,
                document.body
            )}
        </div>
    );
};

export default StatusDropdown;
